import type { FC } from 'react';
import { useCustom } from '@refinedev/core';
import { Table, Tag, Button } from 'antd';
import { Megaphone } from 'lucide-react';
import dayjs from 'dayjs';

export interface Campaign {
  id: string;
  name: string;
  channel: string;         // linkedin | email | blog | multi
  status: string;          // draft | active | paused | completed
  targetPersona: string | null;
  description: string | null;
  startDate: string | null;  // ISO date
  endDate: string | null;
  kpis: Record<string, unknown>;
  contentsCount: number;
  createdAt: string;
}

interface ListCampaignsResponse {
  campaigns: Campaign[];
  total: number;
  page: number;
  limit: number;
}

const CHANNEL_MAP: Record<string, { color: string; label: string }> = {
  linkedin: { color: 'blue', label: 'LinkedIn' },
  email: { color: 'green', label: 'Email' },
  blog: { color: 'orange', label: 'Blog' },
  multi: { color: 'purple', label: 'Multi-canal' },
};

const STATUS_MAP: Record<string, { color: string; label: string }> = {
  draft: { color: 'default', label: 'Brouillon' },
  active: { color: 'blue', label: 'Active' },
  completed: { color: 'green', label: 'Terminée' },
  paused: { color: 'orange', label: 'En pause' },
};

interface CampaignTableProps {
  onCampaignClick: (campaign: Campaign) => void;
  onNewCampaign: () => void;
}

const CampaignTable: FC<CampaignTableProps> = ({ onCampaignClick, onNewCampaign }) => {
  const { data, isLoading } = useCustom<ListCampaignsResponse>({
    url: '/api/v1/admin/marketing/campaigns',
    method: 'get',
    config: { query: { page: 1, limit: 20 } },
  });

  const campaigns = data?.data?.campaigns ?? [];

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (name: string) => <span className="font-medium text-slate-900 dark:text-white">{name}</span>,
    },
    {
      title: 'Canal',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (ch: string) => {
        const c = CHANNEL_MAP[ch] ?? { color: 'default', label: ch };
        return <Tag color={c.color}>{c.label}</Tag>;
      },
    },
    {
      title: 'Dates',
      key: 'dates',
      width: 140,
      render: (_: unknown, r: Campaign) => {
        if (!r.startDate && !r.endDate) return <span className="text-slate-400">—</span>;
        const start = r.startDate ? dayjs(r.startDate).format('DD/MM') : '?';
        const end = r.endDate ? dayjs(r.endDate).format('DD/MM') : '?';
        return <span className="text-xs text-slate-600 dark:text-slate-400">{start} — {end}</span>;
      },
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        const s = STATUS_MAP[status] ?? { color: 'default', label: status };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'Persona',
      dataIndex: 'targetPersona',
      key: 'targetPersona',
      width: 110,
      render: (p: string | null) =>
        p ? <Tag className="text-[10px]">{p.toUpperCase()}</Tag> : <span className="text-slate-400">—</span>,
    },
    {
      title: 'Contenus',
      dataIndex: 'contentsCount',
      key: 'contentsCount',
      width: 80,
      render: (n: number) => n,
    },
  ];

  return (
    <div className="fc-card p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Campagnes</h2>
        <Button type="primary" size="small" icon={<Megaphone className="h-3.5 w-3.5" />} onClick={onNewCampaign}>
          Nouvelle campagne
        </Button>
      </div>
      <Table
        dataSource={campaigns}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize: 10, size: 'small' }}
        size="small"
        scroll={{ x: 600 }}
        onRow={(record) => ({ onClick: () => onCampaignClick(record), className: 'cursor-pointer' })}
      />
    </div>
  );
};

export default CampaignTable;
